/**
 * Utility functions for promotion threshold calculations in Challenge Reports
 *
 * These functions are extracted from ChallengeReport.jsx for better testability
 * and reusability across the application.
 */

/**
 * Calculate accuracy percentage from stats object
 * @param {Object} stats - Stats with total and correct counts
 * @returns {number} Accuracy as percentage (0-100), 0 if invalid
 */
export const calculateAccuracy = (stats) => {
  if (!stats || stats.total === 0) return 0
  const accuracy = (stats.correct / stats.total) * 100
  return isNaN(accuracy) ? 0 : accuracy
}

/**
 * Format score as a whole-number percentage string.
 *
 * Truncates rather than rounds, matching how every other percentage on the Challenge Report is
 * rendered. This file previously used one decimal place while the report page used whole numbers,
 * so the same score read as "85.0%" in the promotion message and "85%" everywhere else.
 *
 * @param {number|null|undefined} score - Score to format
 * @returns {string} Formatted score or "0" if invalid
 */
export const formatScore = (score) => {
  if (score === null || score === undefined || isNaN(score)) return '0'
  return Math.floor(score).toString()
}

/**
 * Check if overall score meets the promotion threshold
 * @param {Object} promotionInfo - Promotion information with criteria
 * @param {number} totalScore - User's total score
 * @returns {boolean} True if threshold is met or no threshold exists
 */
export const checkMeetsOverallThreshold = (promotionInfo, totalScore) => {
  if (!promotionInfo?.criteria?.overallThreshold) return true
  return (totalScore ?? 0) >= promotionInfo.criteria.overallThreshold
}

/**
 * Check if a domain score meets the promotion threshold
 * @param {Object} promotionInfo - Promotion information with criteria
 * @param {number} accuracy - Domain accuracy percentage
 * @returns {boolean} True if threshold is met or no threshold exists
 */
export const checkMeetsDomainThreshold = (promotionInfo, accuracy) => {
  if (!promotionInfo?.criteria?.domainThreshold) return true
  return accuracy >= promotionInfo.criteria.domainThreshold
}

/**
 * Check if all domains meet the threshold (for strategies that require it)
 * @param {Object} promotionInfo - Promotion information with criteria
 * @param {Object} domainStatistics - Domain statistics from report
 * @returns {boolean} True if all domains meet threshold or requirement doesn't apply
 */
export const checkAllDomainsMeetThreshold = (promotionInfo, domainStatistics) => {
  if (!promotionInfo?.criteria?.domainThreshold || !promotionInfo?.criteria?.requireAllDomains) {
    return true
  }
  if (!domainStatistics) return true

  return Object.values(domainStatistics).every((stats) => {
    const accuracy = calculateAccuracy(stats)
    return accuracy >= promotionInfo.criteria.domainThreshold
  })
}

/**
 * Get human-readable description of promotion requirements
 * @param {Object} promotionInfo - Promotion information with criteria
 * @returns {string|null} Description string or null if no criteria
 */
export const getPromotionRequirementsDescription = (promotionInfo) => {
  const criteria = promotionInfo?.criteria
  if (!criteria) return null

  // SAT Mini: 100% required
  if (criteria.overallThreshold === 100 && !criteria.domainThreshold) {
    return 'Perfect score (100%) required for promotion'
  }

  // Regular SAT: 90% overall + 80% per domain
  if (criteria.domainThreshold && criteria.requireAllDomains) {
    return `${criteria.overallThreshold}% overall AND ${criteria.domainThreshold}% minimum in each domain required`
  }

  // Default: just overall threshold
  return `${criteria.overallThreshold}% overall score required`
}

/**
 * Build the grammatical voice used by the promotion messages.
 *
 * The report is readable by tutors, guardians and admins as well as the student who took the
 * challenge, so addressing the reader as "you" misattributes the result to whoever is looking.
 * Passing a subject name switches the same copy to the third person.
 *
 * @param {string|null|undefined} subjectName - the student's name, or null when the reader is the
 *   student themselves
 * @returns {{isThirdPerson: boolean, subject: string, possessive: string,
 *   possessiveMidSentence: string, has: string, their: string, verb: (base: string) => string}}
 */
const buildVoice = (subjectName) => {
  const name = typeof subjectName === 'string' ? subjectName.trim() : ''
  if (!name) {
    return {
      isThirdPerson: false,
      subject: 'You',
      possessive: 'Your',
      possessiveMidSentence: 'your',
      has: "You've",
      their: 'your',
      // Second person takes the bare verb: "You remain", "You scored".
      verb: (base) => base
    }
  }
  return {
    isThirdPerson: true,
    subject: name,
    possessive: `${name}'s`,
    possessiveMidSentence: `${name}'s`,
    has: `${name} has`,
    their: 'their',
    // Third-person singular present takes an -s: "Alex remains". Past tense is unchanged.
    verb: (base) => (base.endsWith('ed') ? base : `${base}s`)
  }
}

/**
 * Generate the promotion status message
 * @param {Object} params - Parameters for generating message
 * @param {Object} params.promotionInfo - Promotion information
 * @param {Object} params.session - Session data with scores and levels
 * @param {Object} params.domainStatistics - Domain statistics from report
 * @param {string} [params.subjectName] - Student's name; when set, the copy is written in the third
 *   person for a tutor/guardian/admin reader instead of addressing them as the test-taker
 * @returns {Object} Message object with type, title, and message
 */
export const getPromotionMessage = ({
  promotionInfo,
  session,
  domainStatistics,
  subjectName = null
}) => {
  const voice = buildVoice(subjectName)
  if (!promotionInfo?.supportsLevelProgression) {
    return {
      type: 'info',
      title: 'Challenge Complete',
      message: 'This challenge type does not include level progression.'
    }
  }

  if (promotionInfo.wasPromoted) {
    return {
      type: 'success',
      title: voice.isThirdPerson ? `${voice.subject} Leveled Up` : 'Congratulations!',
      message: `${voice.has} been promoted to Level ${session?.endingLevel}! ${voice.possessive} performance exceeded all promotion requirements.`
    }
  }

  if (promotionInfo.wasDemoted) {
    return {
      type: 'warning',
      title: 'Level Adjusted',
      message:
        promotionInfo.levelHistory?.reason ||
        `${voice.possessive} level has been adjusted to Level ${session?.endingLevel} based on ${voice.their} performance.`
    }
  }

  // Not promoted - explain why
  const criteria = promotionInfo.criteria
  if (!criteria) {
    return {
      type: 'info',
      title: 'Level Maintained',
      message: `${voice.subject} ${voice.verb('remain')} at Level ${session?.endingLevel}.`
    }
  }

  // If we have a specific reason from the level history, use that
  if (promotionInfo.levelHistory?.reason) {
    return {
      type: 'info',
      title: 'Level Maintained',
      message: promotionInfo.levelHistory.reason
    }
  }

  // Build explanation for why promotion didn't occur
  const reasons = []

  // Check overall threshold - handle 100% (SAT Mini) differently
  if (criteria.overallThreshold && (session?.totalScore ?? 0) < criteria.overallThreshold) {
    if (criteria.overallThreshold === 100) {
      reasons.push(
        `A perfect score (100%) is required for promotion. ${voice.subject} ${voice.verb('scored')} ${formatScore(session?.totalScore)}%.`
      )
    } else {
      reasons.push(
        `${voice.possessive} overall score of ${formatScore(session?.totalScore)}% did not meet the ${criteria.overallThreshold}% threshold.`
      )
    }
  }

  // Check domain thresholds if applicable (only for strategies that require all domains)
  if (criteria.domainThreshold && criteria.requireAllDomains && domainStatistics) {
    const failingDomains = []
    Object.entries(domainStatistics).forEach(([domain, stats]) => {
      const accuracy = calculateAccuracy(stats)
      if (accuracy < criteria.domainThreshold) {
        failingDomains.push(`${domain} (${formatScore(accuracy)}%)`)
      }
    })

    if (failingDomains.length > 0) {
      // Check if overall was met but domains failed
      const overallMet = (session?.totalScore ?? 0) >= criteria.overallThreshold
      if (overallMet) {
        reasons.push(
          `Although ${voice.possessiveMidSentence} overall score met the ${criteria.overallThreshold}% threshold, the following domain(s) did not reach the required ${criteria.domainThreshold}%: ${failingDomains.join(', ')}.`
        )
      } else {
        reasons.push(
          `Additionally, these domain(s) did not meet the ${criteria.domainThreshold}% threshold: ${failingDomains.join(', ')}.`
        )
      }
    }
  }

  return {
    type: 'info',
    title: 'Level Maintained',
    message:
      reasons.length > 0
        ? reasons.join(' ')
        : `${voice.subject} ${voice.verb('remain')} at Level ${session?.endingLevel}. ${
            voice.isThirdPerson
              ? 'More practice will improve their scores!'
              : 'Keep practicing to improve your scores!'
          }`
  }
}
